import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import Document from "documents/document";
import { documentsApi } from "documents/_api/document-api";
import DocumentSchema from "documents/_schema/document-schema";
import Guid from "_shared/guids/guid";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import { cleanupDeletedDocumentReferences as cleanupProposalDeletedDocumentReferences } from "work/proposals/_store/proposals-redux-slice";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import { cleanupDeletedDocumentReferences as cleanupWorkAgreementDeletedDocumentReferences } from "work/work-agreements/_store/work-agreement-redux-slice";

const toDocument = (payload: unknown): Document | undefined => {
  if (DocumentSchema.isValidJSON(payload)) {
    return Document.fromJSON(payload);
  }
  return undefined;
};

const toDocumentId = (payload: unknown): Guid | undefined => {
  if (typeof payload === "string") return Guid.fromJSON(payload) ?? undefined;
  if (payload && typeof payload === "object" && "id" in payload) {
    return Guid.fromJSON((payload as { id?: unknown }).id) ?? undefined;
  }

  return undefined;
};

const callbacks = withCallbacks()
  .add("document-created", (payload: unknown) => (dispatch) => {
    const document = toDocument(payload);
    if (!document) return;
    dispatch(documentsApi.util.invalidateTags([
      { type: "Document", id: document.id.value },
      { type: "Document", id: "LIST" },
    ]));
  })
  // One upload request publishes one of these per recipient, carrying only the ids that recipient
  // may read. Invalidating them together is what keeps a ten-file upload to one list refetch.
  .add("documents-created", (payload: unknown) => (dispatch) => {
    let documentIds: Guid[] | null | undefined;
    try {
      documentIds = Guid.fromJSONArray(payload);
    } catch {
      return;
    }
    if (!documentIds?.length) return;
    dispatch(documentsApi.util.invalidateTags([
      ...documentIds.map((id) => ({ type: "Document" as const, id: id.value })),
      { type: "Document", id: "LIST" },
    ]));
  })
  .add("document-updated", (payload: unknown) => (dispatch) => {
    const document = toDocument(payload);
    if (!document) return;
    dispatch(documentsApi.util.invalidateTags([
      { type: "Document", id: document.id.value },
      { type: "Document", id: "LIST" },
    ]));
  })
  .add("document-deleted", (payload: unknown) => (dispatch) => {
    const documentId = toDocumentId(payload);
    if (!documentId) return;
    dispatch(documentsApi.util.invalidateTags([
      { type: "Document", id: documentId.value },
      { type: "Document", id: "LIST" },
    ]));
    dispatch(cleanupProposalDeletedDocumentReferences(documentId));
    dispatch(cleanupWorkAgreementDeletedDocumentReferences(documentId));
    dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
    dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
  });

export const documentsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.documents,
  connectionHealth: { key: "documents", label: "Documents" },
  shouldConnectionStartImmediately: true,
});

